import React from 'react';
import styled from 'styled-components';

interface ChoiceOption {
  label: string;
  onClick: (value?: string) => void;
  variant?: 'primary' | 'secondary';
  italic?: boolean;
}

export interface TitlePart {
  text: string;
  italic?: boolean;
}

export interface ChoicePanelProps {
  title: string | TitlePart[];
  options: ChoiceOption[];
}

const PanelWrapper = styled.div`
  width: 100%;
  padding: 24px 16px;
  background: linear-gradient(to top, #0a0a0a 80%, transparent);
  position: absolute;
  bottom: 0;
  left: 0;
  box-sizing: border-box;
  z-index: 10;
`;

const Title = styled.h2`
  font-family: 'Lato', sans-serif;
  color: white;
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  letter-spacing: 0;
  text-align: center;
`;

const TitleSpan = styled.span<{ italic?: boolean }>`
  font-style: ${({ italic }) => (italic ? 'italic' : 'normal')};
`;

const Button = styled.button<{
  variant?: 'primary' | 'secondary';
  italic?: boolean;
}>`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  border-radius: 40px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: ${({ variant }) =>
    variant === 'secondary'
      ? '2px solid rgba(72, 52, 212, 1)'
      : '2px solid rgba(51, 156, 77, 1)'};
  background: ${({ variant }) =>
    variant === 'secondary'
      ? `linear-gradient(272.88deg, #686DE0 4.95%, #4834D4 93.62%),
         linear-gradient(180deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 44.64%)`
      : `linear-gradient(275.76deg, #339C4D 44.33%, #55D775 98.56%),
         linear-gradient(180deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 44.64%)`};
  box-shadow: ${({ variant }) =>
    variant === 'secondary'
      ? '0px 4px 20px 0px rgba(104, 109, 224, 0.5)'
      : '0px 4px 20px 0px rgba(51, 156, 77, 0.5)'};

  color: white;
  font-family: 'Lato', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  text-align: center;
  vertical-align: middle;
  font-style: ${({ italic }) => (italic ? 'italic' : 'normal')};
`;

export const ChoicePanel: React.FC<ChoicePanelProps> = ({ title, options }) => {
  return (
    <PanelWrapper>
      <Title>
        {Array.isArray(title)
          ? title.map((part, index) => (
              <TitleSpan key={index} italic={part.italic}>
                {part.text}
              </TitleSpan>
            ))
          : title}
      </Title>
      {options.map((opt, index) => (
        <Button
          key={index}
          onClick={() => opt.onClick(opt.label)}
          variant={opt.variant ?? (index === 1 ? 'secondary' : 'primary')}
          italic={opt.italic}
        >
          {opt.label}
        </Button>
      ))}
    </PanelWrapper>
  );
};
